import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';
import { RiAddLine } from 'react-icons/ri';

const Container = styled.div`
  overflow: hidden;
  height: ${({ containerHeight }) => containerHeight};
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.gray};
  transition:
    height 0.3s ease,
    border 0.3s ease;
  cursor: pointer;
  padding: 1rem;
  user-select: none;

  span {
    transform: rotate(${({ isOpen }) => (isOpen ? '45deg' : 0)});
    color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.gray : theme.colors.white};
    position: absolute;
    right: 0;
    transition: all 0.3s ease;
    font-size: 2rem;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      p:first-child {
        color: ${({ theme }) => theme.colors.gray};
      }
      span {
        color: ${({ theme }) => theme.colors.gray};
        transform: rotate(45deg);
      }
    }
  }
`;

const Title = styled.p`
  ${({ theme }) => theme.mixins.Body};
  color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.gray : theme.colors.white};
  transition: color 0.3s ease;
  ${({ theme }) => theme.mixins.flexRow};
  justify-content: flex-start;
  position: relative;
`;

const Description = styled.p`
  text-align: justify;
  ${({ theme }) => theme.mixins.Body};
  color: ${({ theme }) => theme.colors.white};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease;
  margin-top: ${({ isOpen }) => (isOpen ? '2rem' : '0')};
  padding-bottom: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
  max-width: 95%;

  /* Ensure it's removed from flow when closed */
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const InfoTab = ({ content, isOpen, onToggle }) => {
  const { title, description } = content;
  const containerRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState('auto');

  const calculateHeight = useCallback(() => {
    if (contentWrapperRef.current && containerRef.current) {
      const contentHeight = contentWrapperRef.current.scrollHeight;
      const containerStyles = getComputedStyle(containerRef.current);
      const paddingTop = parseFloat(containerStyles.paddingTop);
      const paddingBottom = parseFloat(containerStyles.paddingBottom);
      const paddingTopBottom = paddingTop + paddingBottom;

      if (isOpen) {
        setContainerHeight(`${contentHeight + paddingTopBottom}px`);
      } else {
        const titleHeight = contentWrapperRef.current.children[0].scrollHeight;
        setContainerHeight(`${titleHeight + paddingTopBottom}px`);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    // Run height calculation only when open state changes
    calculateHeight();

    // Recalculate when window resizes
    const onResize = () => calculateHeight();
    window.addEventListener('resize', onResize);

    // Ensure we recalc only *after* height transition finishes
    const handleTransitionEnd = () => calculateHeight();
    const node = containerRef.current;
    node?.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      window.removeEventListener('resize', onResize);
      node?.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [isOpen, calculateHeight]);

  return (
    <Container
      ref={containerRef}
      onClick={onToggle}
      role="button"
      aria-expanded={isOpen}
      aria-controls={`panel-${title}`}
      containerHeight={containerHeight}
      isOpen={isOpen}
    >
      <div ref={contentWrapperRef}>
        <Title isOpen={isOpen}>
          {title}
          <span>
            <RiAddLine aria-hidden="true" />
          </span>
        </Title>
        <Description id={`panel-${title}`} isOpen={isOpen}>
          {description}
        </Description>
      </div>
    </Container>
  );
};

InfoTab.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default InfoTab;
